import reducerRegistry from '@comeon/reducer-registry';
import { fetchJson, postJson } from '~/core/ajax-json.js';
import REDUCER_NAME from './reducer-name.js';
import { startRealityCheckNetherlands } from '../reality-check-utils-netherlands';
import { getCurrentDate } from '../reality-check-utils';
import { sendAppBridgeEvent, WEB_EVENTS } from '~/components/app-bridge';

export const REALITY_CHECK_TIMER_STARTED = '@komigen/mobile-frontend/reality-check/REALITY_CHECK_TIMER_STARTED';
export const REALITY_CHECK_DISABLED = '@komigen/mobile-frontend/reality-check/REALITY_CHECK_DISABLED';
export const REALITY_CHECK_COOLOFF_TIMER_STARTED =
	'@komigen/mobile-frontend/reality-check/REALITY_CHECK_COOLOFF_TIMER_STARTED';
export const REALITY_CHECK_DURATION_UPDATE = '@komigen/mobile-frontend/reality-check/REALITY_CHECK_DURATION_UPDATE';
export const REALITY_CHECK_GAME_CLOSED = '@komigen/mobile-frontend/reality-check/REALITY_CHECK_GAME_CLOSED';
export const REALITY_CHECK_GAME_STARTED = '@komigen/mobile-frontend/reality-check/REALITY_CHECK_GAME_STARTED';
export const REALITY_CHECK_TIME_REACHED = '@komigen/mobile-frontend/reality-check/REALITY_CHECK_TIME_REACHED';
export const REALITY_CHECK_TIME_REACHED_WEBSOCKET =
	'@komigen/mobile-frontend/reality-check/REALITY_CHECK_TIME_REACHED_WEBSOCKET';
export const REALITY_CHECK_TIME_REACHED_ON_LOAD =
	'@komigen/mobile-frontend/reality-check/REALITY_CHECK_TIME_REACHED_ON_LOAD';
export const REALITY_CHECK_COOL_OFF_TIME_REACHED_WEBSOCKET =
	'@komigen/mobile-frontend/reality-check/REALITY_CHECK_COOL_OFF_TIME_REACHED_WEBSOCKET';
export const REALITY_CHECK_COOLOFF_SERVICE_NOTIFIED =
	'@komigen/mobile-frontend/reality-check/REALITY_CHECK_COOLOFF_SERVICE_NOTIFIED';
export const REALITY_CHECK_SHOW_PANIC_REASON = '@komigen/mobile-frontend/reality-check/REALITY_CHECK_SHOW_PANIC_REASON';
export const REALITY_CHECK_GAME_PLAY = '@komigen/mobile-frontend/reality-check/REALITY_CHECK_GAME_PLAY';
export const REALITY_CHECK_ERROR = '@komigen/mobile-frontend/reality-check/REALITY_CHECK_ERROR';

async function setDuration(duration) {
	try {
		const data = { duration: parseInt(duration, 10) };
		return await postJson('/player/limits/reality-check', data);
	} catch (err) {
		return undefined;
	}
}

function durationUpdate(duration, onSuccessCallback, onFailCallback) {
	return async dispatch => {
		try {
			const response = await setDuration(duration);
			if (response.result !== undefined) {
				onSuccessCallback(response.result);
				dispatch({
					type: REALITY_CHECK_DURATION_UPDATE,
					payload: duration
				});
			} else {
				onFailCallback();
			}
		} catch (err) {
			onFailCallback();
		}
	};
}

async function getDuration() {
	try {
		const response = await fetchJson('/player/limits/reality-check/duration');
		return response.result !== undefined && response.result.timerDuration !== null
			? response.result.timerDuration
			: -1;
	} catch (err) {
		return -1;
	}
}

function enableRealityCheckPopup() {
	return {
		type: REALITY_CHECK_GAME_CLOSED
	};
}

function disableRealityCheckPopup() {
	return {
		type: REALITY_CHECK_GAME_STARTED
	};
}
function updateDurationAction(duration) {
	return {
		type: REALITY_CHECK_DURATION_UPDATE,
		payload: duration
	};
}

function realityCheckReachedOnLoad(payload) {
	return {
		type: REALITY_CHECK_TIME_REACHED_ON_LOAD,
		payload
	};
}

const timeLimitExceededLog = async payload => {
	await postJson('/player/limit/logRgCrossed', payload);
};

const timeLimitExceededPlayerNotes = async payload => {
	await postJson('/player/limit/logPlayerNotes', payload);
};

const realityCheckTimeReachedToggle = isTimeReached => {
	return async dispatch => {
		dispatch({
			type: REALITY_CHECK_TIME_REACHED,
			payload: isTimeReached
		});
	};
};

// This delay is implemented for an edge case where game gets rc limit error before we show popup
const RC_POPUP_DELAY = 2000;

// TODO: cleanup after new refactored UI is toggled ON
function start(timestamp, showCallback) {
	return async (dispatch, getState) => {
		const { timerId: oldTimerId } = getState().realityCheck;
		const duration = await getDuration();
		const durationInMilliSec = duration * 60 * 1000;
		const now = await getCurrentDate();
		let remainingDuration = durationInMilliSec;
		let newTimerId = oldTimerId;
		if (now === undefined) {
			return;
		}
		clearTimeout(oldTimerId);
		if (duration > 0) {
			remainingDuration = durationInMilliSec - (now - timestamp);
			remainingDuration = remainingDuration > 0 ? remainingDuration : 2000;

			newTimerId = setTimeout(() => {
				if (showCallback !== undefined) {
					showCallback(duration);
				}
			}, remainingDuration - RC_POPUP_DELAY);

			setTimeout(() => {
				dispatch(realityCheckTimeReachedToggle(true));
			}, Math.max(remainingDuration, 0) - RC_POPUP_DELAY);
		}

		dispatch({
			type: REALITY_CHECK_TIMER_STARTED,
			payload: {
				timerId: newTimerId,
				duration
			}
		});
	};
}

function startGermany(timestamp) {
	return async (dispatch, getState) => {
		const { timerId: oldTimerId } = getState().realityCheck;
		const duration = await getDuration();
		const durationInMilliseconds = duration * 60 * 1000;
		const now = await getCurrentDate();
		let remainingDuration = durationInMilliseconds;
		let newTimerId = oldTimerId;
		if (now === undefined) {
			return;
		}
		clearTimeout(oldTimerId);
		if (timestamp) {
			remainingDuration = durationInMilliseconds - (now - timestamp);
		}

		remainingDuration = remainingDuration > 0 ? remainingDuration : 2000;

		newTimerId = setTimeout(() => {
			dispatch(realityCheckTimeReachedToggle(true));
		}, remainingDuration);

		dispatch({
			type: REALITY_CHECK_TIMER_STARTED,
			payload: {
				timerId: newTimerId
			}
		});

		sendAppBridgeEvent(WEB_EVENTS.LOG_EVENT, [
			{
				log: `StartGermany:
				remainingDuration: ${remainingDuration},
				timestamp: ${timestamp},
				duration: ${duration},
				now: ${now}`
			}
		]);
	};
}

const startGermanyWebSocket = timestamp => async dispatch => {
	const duration = await getDuration();
	const durationInMilliseconds = duration * 60 * 1000;
	const now = await getCurrentDate();
	let remainingDuration = durationInMilliseconds;
	if (now && timestamp) {
		remainingDuration = durationInMilliseconds - (now - timestamp);
		if (remainingDuration <= 0) {
			dispatch(realityCheckTimeReachedToggle(true));
		}
	}
};

function startNetherlands({ displayNotification, recurring = false, newNotificationInfo = {} }) {
	return async (dispatch, getState) => {
		const { timerId: oldTimerId } = getState().realityCheck;
		let remainingDuration;
		let notificationText;
		let notificationId;

		// when we already have the info for next notification
		if (recurring) {
			remainingDuration = newNotificationInfo.delayMillis;
			notificationText = newNotificationInfo.text;
			notificationId = newNotificationInfo.ordinal;
		}

		// when starting RC for first time
		if (!oldTimerId && !recurring) {
			// starts RC on server and returns next popup info
			const response = await startRealityCheckNetherlands();
			if (response.status === 'SUCCESS') {
				const popupInfo = response.result;
				if (popupInfo) {
					remainingDuration = popupInfo.delayMillis;
					notificationText = popupInfo.text;
					notificationId = popupInfo.ordinal;
				} else return dispatch({ type: REALITY_CHECK_ERROR });
			} else {
				return dispatch({ type: REALITY_CHECK_ERROR });
			}
		}

		let newTimerId = oldTimerId;

		clearTimeout(oldTimerId);
		if (remainingDuration >= 0) {
			newTimerId = setTimeout(() => {
				// display notification
				if (displayNotification !== undefined && typeof displayNotification === 'function') {
					displayNotification(notificationText, notificationId);
				}
			}, remainingDuration);
		}

		dispatch({
			type: REALITY_CHECK_TIMER_STARTED,
			payload: {
				timerId: newTimerId,
				notificationId,
				notificationText
			}
		});
	};
}

const showRCPopup = ({ duration, playTimeHours, playTimeMinutes, betSum, totalWagered, totalWon, notLosing }) => ({
	type: REALITY_CHECK_TIME_REACHED_WEBSOCKET,
	payload: {
		duration,
		playTimeHours,
		playTimeMinutes,
		betSum,
		totalWagered,
		totalWon,
		notLosing
	}
});

const startRCAfterCoolOff = () => ({
	type: REALITY_CHECK_COOL_OFF_TIME_REACHED_WEBSOCKET
});

const realityCheckCoolOffTimerStart = coolOffTimerId => {
	return async dispatch => {
		dispatch({
			type: REALITY_CHECK_COOLOFF_TIMER_STARTED,
			payload: coolOffTimerId
		});
	};
};

const toggleCoolOffPeriod = (on, timestamp = null) => {
	return async dispatch => {
		dispatch({
			type: REALITY_CHECK_COOLOFF_SERVICE_NOTIFIED,
			payload: { on, timestamp }
		});
	};
};

const showPanicReason = token => ({
	type: REALITY_CHECK_SHOW_PANIC_REASON,
	payload: { panicReason: true, panicToken: token }
});

const hidePanicReason = () => ({
	type: REALITY_CHECK_SHOW_PANIC_REASON,
	payload: { panicReason: false, panicToken: '' }
});

const startGamePlay = () => ({
	type: REALITY_CHECK_GAME_PLAY,
	payload: { gamePlayStarted: true }
});

const endGamePlay = () => ({
	type: REALITY_CHECK_GAME_PLAY,
	payload: { gamePlayStarted: false, timerId: undefined }
});

const preloadedState = {
	duration: -1,
	timerId: undefined,
	isInGame: false,
	realityCheckTimeReached: false,
	realityCheckTimeReachedFromWebsocket: false,
	coolOffPeriodOn: false,
	coolOffTimeStamp: null,
	panicReason: false,
	panicToken: ''
};

function reducer(state = preloadedState, { type, payload }) {
	switch (type) {
		case REALITY_CHECK_TIMER_STARTED:
			return {
				...state,
				timerId: payload.timerId,
				duration: payload.duration,
				popupId: payload.popupId,
				notificationText: payload.text,
				realityCheckTimeReached: false
			};
		case REALITY_CHECK_DISABLED:
			return {
				...state,
				timerId: undefined,
				duration: -1,
				realityCheckTimeReached: false
			};
		case REALITY_CHECK_COOLOFF_TIMER_STARTED:
			return {
				...state,
				coolOffTimerId: payload
			};
		case REALITY_CHECK_DURATION_UPDATE:
			return {
				...state,
				duration: payload
			};
		case REALITY_CHECK_GAME_STARTED:
			return {
				...state,
				isInGame: true
			};
		case REALITY_CHECK_GAME_CLOSED:
			return {
				...state,
				isInGame: false
			};
		case REALITY_CHECK_TIME_REACHED:
			return {
				...state,
				realityCheckTimeReached: payload,
				realityCheckTimeReachedFromWebsocket: payload
			};
		case REALITY_CHECK_TIME_REACHED_WEBSOCKET:
			return {
				...state,
				duration: payload.duration,
				realityCheckTimeReachedFromWebsocket: true,
				realityCheckTimeReached: true,
				betSum: payload.betSum,
				totalWagered: payload.totalWagered,
				totalWon: payload.totalWon,
				playTimeHours: payload.playTimeHours,
				playTimeMinutes: payload.playTimeMinutes,
				notLosing: payload.notLosing,
				realityCheckTimeCoolOffReachedFromWebsocket: false
			};
		case REALITY_CHECK_TIME_REACHED_ON_LOAD:
			return {
				...state,
				realityCheckTimeReached: true,
				betSum: payload.betSum,
				playTimeHours: payload.playTimeHours,
				playTimeMinutes: payload.playTimeMinutes,
				notLosing: payload.notLosing
			};
		case REALITY_CHECK_COOL_OFF_TIME_REACHED_WEBSOCKET:
			return {
				...state,
				realityCheckTimeCoolOffReachedFromWebsocket: true
			};
		case REALITY_CHECK_COOLOFF_SERVICE_NOTIFIED:
			return {
				...state,
				coolOffPeriodOn: payload.on,
				coolOffTimeStamp: payload.timestamp,
				realityCheckTimeCoolOffReachedFromWebsocket: false
			};
		case REALITY_CHECK_SHOW_PANIC_REASON:
			return {
				...state,
				panicReason: payload.panicReason,
				panicToken: payload.panicToken
			};
		case REALITY_CHECK_GAME_PLAY:
			return {
				...state,
				gamePlayStarted: payload.gamePlayStarted
			};
		case REALITY_CHECK_ERROR:
			return {
				...state,
				apiError: true
			};
		default:
			return state;
	}
}

reducerRegistry.register(REDUCER_NAME, reducer);
export {
	start,
	startGermany,
	startGermanyWebSocket,
	startNetherlands,
	getDuration,
	durationUpdate,
	disableRealityCheckPopup,
	enableRealityCheckPopup,
	realityCheckTimeReachedToggle,
	timeLimitExceededLog,
	timeLimitExceededPlayerNotes,
	toggleCoolOffPeriod,
	realityCheckCoolOffTimerStart,
	showPanicReason,
	hidePanicReason,
	showRCPopup,
	updateDurationAction,
	realityCheckReachedOnLoad,
	startRCAfterCoolOff,
	startGamePlay,
	endGamePlay
};

export default reducer;
